import Pagination from '../../components/Common/Pagination';
import React, { useState, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import { CustomCalendar } from '../../components/Common/CustomCalendar';

interface PopItem {
  id: number;
  name: string;
  collateral: string;
  collateralType: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Inactive' | 'Upcoming';
}

export const PopList: React.FC = () => {
  const [popItems, setPopItems] = useState<PopItem[]>([
    {
      id: 1,
      name: 'Hello',
      collateral: 'View Collateral',
      collateralType: 'Image',
      startDate: '2026-07-31 15:53:00',
      endDate: '2026-07-15 15:53:00',
      status: 'Upcoming',
    }
  ]);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [_editingItem, setEditingItem] = useState<PopItem | null>(null);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    collateralType: 'Video',
    level: 'Company',
    fromDate: '2026-03-28',
    endDate: '2026-03-28',
    showAttendance: false,
  });

  const activeCounts = 445; // Fixed mock count as shown in figma
  const inactiveCounts = 445; // Fixed mock count as shown in figma

  // Filtering Logic
  const filteredPopItems = useMemo(() => {
    return popItems.filter((item) => {
      if (activeTab === 'Active' && item.status !== 'Active') return false;
      if (activeTab === 'Inactive' && item.status !== 'Inactive') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.collateralType.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [popItems, activeTab, searchQuery]);

  // Paginated Items
  const paginatedPopItems = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredPopItems.slice(startIndex, startIndex + entriesLimit);
  }, [filteredPopItems, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      collateralType: 'Video',
      level: 'Company',
      fromDate: '2026-03-28',
      endDate: '2026-03-28',
      showAttendance: false,
    });
    setView('add');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required.');
      return;
    }

    const newItem: PopItem = {
      id: popItems.length > 0 ? Math.max(...popItems.map(p => p.id)) + 1 : 1,
      name: formData.name,
      collateral: 'View Collateral',
      collateralType: formData.collateralType,
      startDate: `${formData.fromDate} 00:00:00`,
      endDate: `${formData.endDate} 00:00:00`,
      status: 'Active',
    };

    setPopItems([...popItems, newItem]);
    setView('list');
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button 
            type="button"
            onClick={() => setView('list')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Add POP
          </h1>
        </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Collateral Type Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-0.5">Collateral Type</label>
              <span className="block text-[10px] text-slate-500 mb-1">Video, Image, Hyperlink, Pdf, Thumbnail</span>
              <div className="relative">
                <select
                  value={formData.collateralType}
                  onChange={(e) => setFormData({...formData, collateralType: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-700 appearance-none bg-white"
                >
                  <option value="Video">Video</option>
                  <option value="Image">Image</option>
                  <option value="Hyperlink">Hyperlink</option>
                  <option value="Pdf">Pdf</option>
                  <option value="Thumbnail">Thumbnail</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Level Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Level</label>
              <div className="relative">
                <select
                  value={formData.level}
                  onChange={(e) => setFormData({...formData, level: e.target.value})}
                  className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-750 appearance-none bg-[#ECECF2]/50 cursor-pointer"
                >
                  <option value="Company">Company</option>
                  <option value="Branch">Branch</option>
                  <option value="Warehouse">Warehouse</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Thumbnail for Collateral */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Thumbnail for Collateral</label>
              <div className="flex items-center border border-[#828282] rounded-2xl overflow-hidden bg-white">
                <button 
                  type="button" 
                  className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Upload
                </button>
                <input 
                  type="text" 
                  readOnly
                  placeholder="Choose file" 
                  className="flex-1 bg-transparent px-3 text-xs text-slate-500 outline-none cursor-default font-semibold"
                />
                <button 
                  type="button" 
                  className="bg-[#FFF8E1] text-slate-700 hover:bg-[#FFF3CD] text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Browse
                </button>
              </div>
            </div>

            {/* From Date */}
            <CustomCalendar
              label="From Date"
              value={formData.fromDate}
              onChange={(val) => setFormData({ ...formData, fromDate: val })}
            />

            {/* End Date */}
            <CustomCalendar
              label="End Date"
              value={formData.endDate}
              minDate={formData.fromDate}
              onChange={(val) => setFormData({ ...formData, endDate: val })}
            />

            {/* Show Attendance Checkbox */}
            <div className="col-span-1 md:col-span-2 flex items-center gap-2 select-none pt-2">
              <input 
                type="checkbox" 
                id="showAttendance"
                checked={formData.showAttendance}
                onChange={(e) => setFormData({...formData, showAttendance: e.target.checked})}
                className="rounded go_erp-text focus:ring-[#C62828] w-4 h-4 cursor-pointer"
              />
              <label htmlFor="showAttendance" className="text-xs font-bold text-slate-700 cursor-pointer">Show Attendance</label>
            </div>
          </div>

        </form>
          {/* Submit */}
          <div className="flex justify-center pt-4">
            <button 
              type="submit" 
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20"
            >
              Submit
            </button>
          </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['ID', 'Name', 'Collateral', 'Collateral Type', 'Start Date', 'End Date', 'Status'];
    const rows = filteredPopItems.map((item) => [
      item.id,
      item.name,
      item.collateral,
      item.collateralType,
      item.startDate,
      item.endDate,
      item.status,
    ]);
    exportToCSV(`pop_items_${activeTab.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            POP
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-655 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button className="bg-[#5D4037] hover:bg-[#4E342E] text-white text-sm px-4 py-2.5 rounded-xl cursor-pointer transition-colors shadow-sm">
            Expired
          </button>
          <button className="bg-[#F9C327] hover:bg-[#E5B21E] text-white text-sm px-4 py-2.5 rounded-xl cursor-pointer transition-colors shadow-sm">
            Upcoming
          </button>
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add POP
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 px-4 outline-none focus:border-slate-400 w-44 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Collateral</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Collateral Type</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Start Date</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">End Date</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {paginatedPopItems.length > 0 ? (
                paginatedPopItems.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.id}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.name}</td>
                    <td className="px-4 py-4 text-xs font-medium">
                      <span className="underline cursor-pointer text-[#828282] hover:text-slate-800">
                        {item.collateral}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.collateralType}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.startDate}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.endDate}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.status}</td>
                    <td className="px-4 py-4 text-xs text-left">
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-14 text-center text-xs font-semibold text-slate-400">
                    No Data Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredPopItems.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default PopList;
